import { useEffect, useId, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { DetailsDialog } from '@truvhq/ui';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAuth } from 'shared/lib/auth/useAuth';
import { submitSupportTicket } from 'shared/lib/support/submitSupportTicket';

const schema = z.object({
  subject: z.string().trim().min(1, 'Please give the ticket a subject'),
  description: z.string().trim().min(1, 'Please describe your issue'),
  includeTranscript: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

const DEFAULT_VALUES: FormValues = { subject: '', description: '', includeTranscript: true };

const triggerSx = { px: 0.75, py: 0.5, fontSize: '0.8125rem', textTransform: 'none' } as const;

type SupportTicketButtonProps = {
  /** Read on submit, so the transcript covers everything said right up to that point. */
  getTranscript: () => string;
};

export function SupportTicketButton({ getTranscript }: SupportTicketButtonProps) {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULT_VALUES });

  useEffect(() => {
    if (isOpen) {
      reset(DEFAULT_VALUES);
      setFile(null);
      setError(null);
    }
  }, [isOpen, reset]);

  // Closing mid-flight would drop the user's text while the request is still going.
  const close = () => {
    if (!isSubmitting) {
      setIsOpen(false);
    }
  };

  const onSubmit = async ({ subject, description, includeTranscript }: FormValues) => {
    setError(null);
    try {
      await submitSupportTicket({
        fullName: user?.name ?? '',
        email: user?.email ?? '',
        subject,
        description,
        file,
        transcript: includeTranscript ? getTranscript() : undefined,
      });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to submit ticket. Please try again.');
      return;
    }
    setIsOpen(false);
    enqueueSnackbar('Support ticket created. Our team will respond via email.');
  };

  return (
    <>
      <Button variant="text" sx={triggerSx} onClick={() => setIsOpen(true)}>
        Create support ticket
      </Button>
      <DetailsDialog
        open={isOpen}
        title="Create support ticket"
        subtitle="Submit a ticket and our support team will get back to you."
        onClose={close}
        content={
          <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
            {error && <Alert severity="error">{error}</Alert>}
            <Controller
              control={control}
              name="subject"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  label="Subject"
                  placeholder="Brief summary of your issue"
                />
              )}
            />
            <Controller
              control={control}
              name="description"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  label="Description"
                  maxRows={8}
                  minRows={4}
                  placeholder="Please describe your issue in detail"
                />
              )}
            />
            <div className="flex items-center gap-3">
              <Button component="label" htmlFor={fileInputId} variant="outlined">
                Attach a file
              </Button>
              <input
                className="hidden"
                id={fileInputId}
                type="file"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
              {file && (
                <Typography className="min-w-0 truncate text-grey60" variant="body2">
                  {file.name}
                </Typography>
              )}
            </div>
            <Controller
              control={control}
              name="includeTranscript"
              render={({ field: { value, ...field } }) => (
                <FormControlLabel control={<Checkbox {...field} checked={value} />} label="Include chat transcript" />
              )}
            />
            <div className="flex justify-end gap-2">
              <Button disabled={isSubmitting} variant="outlined" onClick={close}>
                Cancel
              </Button>
              <LoadingButton loading={isSubmitting} type="submit" variant="contained">
                Submit ticket
              </LoadingButton>
            </div>
          </form>
        }
      />
    </>
  );
}
